// utils/api.js
import JSEncrypt from "jsencrypt";
import CryptoJS from "crypto-js";

// Get API URL from environment variables
const API_URL = 'https://awiss.me/api/api.php';

const RSA_PUBLIC_KEY = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAv1tX0H/gPAs6zseq0LQI
02YzT0XZ8LGsMf8r5d8x8QEV6ESX4MRkk1gGeMCOzlnjpHwuJGyFkp19M/N7vNtC
ceUXKM3z456m+2059FKvoqp3/u1h/19A3k7oWqpOJ/1ooJ3t1QPLbkxCWVunJxXl
P1AQBiqIMQJO6xDB2EXF/ILEaFqAuxIF2RJ9OMb5vBof/INFuHJzpiE2fhUzC7Lj
CdXivDs1l9r3nrU5XpiM8x+1d7lx8uSjgIITAjPSw09FYihv5PyW6HyRwGbOWKN9
EHX4T9MuzYbuNg6ZZ3yvnQon3ubYhhJ/ZQGrTijI+eUzNyk7CfGX4rTtfNM3fzpI
0QIDAQAB
-----END PUBLIC KEY-----`;

/* ===========================
   UTILS
=========================== */
const forceLogout = () => {
    console.log('Force logout initiated');

    // Clear all localStorage data
    localStorage.clear();
    console.log('localStorage cleared');

    // Clear sessionStorage if you're using it
    sessionStorage.clear();

    // Dispatch a custom event that your app can listen for
    window.dispatchEvent(new CustomEvent('userLoggedOut'));

    // Navigate to login page (using react-router-dom)
    // This will be handled in your routing setup
    if (window.location.pathname !== '/login') {
        window.location.href = '/login';
    }
};

const generateAESKey = (length = 16) => {
    const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let key = "";
    for (let i = 0; i < length; i++) {
        key += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return key;
};

/* ===========================
   APP META
=========================== */
const getAppMeta = () => {
    return {
        appPlatform: 'web',
        appVersion: "1.0.0",
        buildNumber: "1",
    };
};

/* ===========================
   HYBRID ENCRYPTION (AES-128)
=========================== */
export const encryptDataHybrid = (jsonData) => {
    try {
        // 1️⃣ Generate 16-char AES key (AES-128)
        const aesKey = generateAESKey(16);
        const aesKeyWord = CryptoJS.enc.Utf8.parse(aesKey);

        // 2️⃣ AES-128-ECB encrypt
        const encryptedData = CryptoJS.AES.encrypt(
            JSON.stringify(jsonData),
            aesKeyWord,
            {
                mode: CryptoJS.mode.ECB,
                padding: CryptoJS.pad.Pkcs7,
            }
        ).toString(); // Base64

        // 3️⃣ RSA encrypt AES key (PKCS1 v1.5)
        const rsa = new JSEncrypt();
        rsa.setPublicKey(RSA_PUBLIC_KEY);

        const encryptedKey = rsa.encrypt(aesKey);
        if (!encryptedKey) throw new Error("RSA encryption failed");

        return {
            encryptedData,
            encryptedKey,
        };
    } catch (error) {
        console.error("❌ Encryption failed:", error);
        return null;
    }
};

/* ===========================
   API CALL
=========================== */
export const apiCall = async (
    payload = {},
    isLoggedIn = true,
    header = {},
) => {
    try {
        let user_id = "";
        let token = "";

        if (isLoggedIn) {
            const storedUserData = localStorage.getItem("session_data");
            if (!storedUserData) {
                forceLogout();
                throw new Error("User not logged in");
            }

            const parsedData = JSON.parse(storedUserData);
            if (!parsedData?.user_id || !parsedData?.token) {
                forceLogout();
                throw new Error("Invalid user data");
            }

            user_id = parsedData.user_id;
            token = parsedData.token;
        }

        const { appPlatform, appVersion, buildNumber } = getAppMeta();

        // 🔐 Encrypt full request
        const encrypted = encryptDataHybrid({
            appPlatform,
            appVersion,
            buildNumber,
            user_id,
            token,
            payload,
        });

        if (!encrypted) throw new Error("Encryption failed");

        const timestamp = Math.floor(Date.now() / 1000);

        const response = await fetch(`${API_URL}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${timestamp * 784116}`,
                ...header,
            },
            body: JSON.stringify({
                encryptedData: encrypted.encryptedData,
                encryptedKey: encrypted.encryptedKey,
            }),
        });

        if (!response.ok) throw new Error(`Server error: ${response.status}`);

        const result = await response.json();

        if (result.response_code === 7) {
            console.log("Session expired - logging out");
            forceLogout();
            throw new Error("Session expired");
        }

        if (result.response_code === 6 && result.message) {
            // Open URL in new tab for web
            window.open(result.message, '_blank');
        }

        return result;
    } catch (error) {
        console.error("API call error:", error);
        throw error; // Re-throw to allow handling in components
    }
};